import * as Sentry from '@sentry/react';

import { store } from 'redux/store';
import {
  selectTokenTimeOut,
  selectTokenTimeStamp,
  selectTokenUserName,
  selectUserId,
  selectUserIsAdmin,
} from 'redux/reducers/authReducer';
import { ResponseError } from 'services/api/base';
import stringifyError from 'helper/stringifyError';
import { addSeconds, format, fromUnixTime } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// `confirm` é o hash da confirmação de senha (PUT /user-reset-password) e `refresh_token`
// vai no corpo do refresh: ambos apareciam em "Arguments" nos eventos do Sentry.
const PARAM_BLOCKLIST = ['product_key', 'password', 'confirm', 'refresh_token', 'reset_token'];
const TIMESTAMP_FORMAT = 'P HH:mm:ss.SSS';

// Falha de rede não é bug da aplicação: um usuário com a conexão instável dispara vários
// erros no mesmo instante (um por requisição). Vira UM evento por janela; o resto vai como
// breadcrumb, que o Sentry anexa ao próximo evento.
const NETWORK_STATUSES = ['FETCH_ERROR', 'TIMEOUT_ERROR'];
export const NETWORK_EVENT_WINDOW_MS = 10_000;
let lastNetworkEventAt = 0;

export function resetNetworkEventThrottle() {
  lastNetworkEventAt = 0;
}

// `navigator.connection` ainda não está no lib.dom do TypeScript e não existe no Safari/Firefox.
function getNetworkInfo(retries: number | undefined) {
  const connection = (navigator as any).connection || {};

  return {
    online: navigator.onLine,
    effectiveType: connection.effectiveType,
    rtt: connection.rtt,
    downlink: connection.downlink,
    saveData: connection.saveData,
    visibility: document.visibilityState,
    retries: retries ?? 0,
  };
}

function getRequestData(error: ResponseError) {
  const method = error.meta?.method || '';
  const query = {};
  let url: string;
  let hostname: string;
  let resource: string;
  let productKey: string;

  try {
    const parsedUrl = new URL(error.meta?.url as string);

    url = `${parsedUrl.origin}${parsedUrl.pathname}`;
    hostname = parsedUrl.hostname;
    resource = parsedUrl.pathname;
    productKey = parsedUrl.searchParams.get('product_key') || '';

    parsedUrl.searchParams.forEach((value, key) => {
      if (!PARAM_BLOCKLIST.includes(key)) query[key] = value;
    });
  } catch (e) {
    url = '';
    hostname = '';
    resource = '';
    productKey = '';
  }

  return {
    url,
    hostname,
    resource,
    method,
    query,
    productKey,
  };
}

function parseData(data: any) {
  let title = 'NO DATA';
  let description = 'Description missing';

  if (data instanceof Object) {
    if (typeof data.description === 'string') {
      const parts = data
        .description
        .split(':')
        .map((l) => l.trim())
        .filter((l) => l);
      [title] = parts;
      description = parts.join(': ');
    } else {
      title = Object.keys(data).join(', ');
      description = Object
        .entries(data)
        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
        .join('\n');
    }
  }

  if (typeof data === 'string') {
    const lines = data
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l);
    [title] = lines;
    description = lines.join('\n');
  }

  return { title, description };
}

function getUserData() {
  const state = store.getState();
  const id = selectUserId(state).toString();
  const username = selectTokenUserName(state);
  const userIsAdmin = selectUserIsAdmin(state);

  return { id, username, userIsAdmin };
}

function getProduct(productKey: string) {
  if (!productKey) return '';

  const productKeys = { ...store.getState().productKeys };
  let product = '';
  Object.keys(productKeys).forEach((key) => {
    if (productKey === productKeys[key]) product = key;
  });
  return product;
}

function getAuthData() {
  const state = store.getState();

  const tokenTimestamp = selectTokenTimeStamp(state);
  const authenticatedAt = fromUnixTime(tokenTimestamp);
  const formattedAuthenticatedAt = format(authenticatedAt, TIMESTAMP_FORMAT, { locale: ptBR });

  const tokenTimout = selectTokenTimeOut(state);
  const expiresAt = addSeconds(authenticatedAt, tokenTimout);
  const formattedExpiredAt = format(expiresAt, TIMESTAMP_FORMAT, { locale: ptBR });

  return { formattedAuthenticatedAt, formattedExpiredAt };
}

function getArgs(error: ResponseError) {
  if (!error.meta) return undefined;

  if (typeof error.meta.args !== 'object') return error.meta.args;

  const args = { ...error.meta.args };
  PARAM_BLOCKLIST.forEach((k) => delete args[k]);

  return args;
}

export default function sendErrorToSentry(error: ResponseError) {
  const status = error.status.toString();
  const { title, description } = parseData(error.data);
  const fullTitle = `[ERROR PAGE][STATUS ${status}] ${title}`;
  const {
    url,
    hostname,
    resource,
    method,
    query,
    productKey,
  } = getRequestData(error);
  const isNetworkError = NETWORK_STATUSES.includes(status);

  if (isNetworkError) {
    const now = Date.now();
    if (now - lastNetworkEventAt < NETWORK_EVENT_WINDOW_MS) {
      Sentry.addBreadcrumb({
        category: 'network',
        level: 'error',
        message: `${method} ${resource} ${status}`,
      });
      return;
    }
    lastNetworkEventAt = now;
  }

  const { id, username, userIsAdmin } = getUserData();
  const { formattedAuthenticatedAt, formattedExpiredAt } = getAuthData();
  const args = getArgs(error);

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    // Rede: uma issue só por host (o endpoint vai na tag `resource`); o resto continua
    // agrupando por endpoint, que é o que separa um bug de outro.
    scope.setFingerprint(
      isNetworkError ? ['network', hostname, status] : [hostname, resource, status, title],
    );
    scope.setTransactionName(resource);
    scope.setUser({
      id,
      username,
      email: username,
      Admin: userIsAdmin ? '✅' : '❌',
    });
    scope.setContext('Response Details', {
      URL: hostname,
      Resource: resource,
      Method: method,
      Code: status,
      Description: description,
      Query: query,
      Arguments: args,
      Retries: error.meta?.retries ?? 0,
    });
    scope.setContext('Auth Details', {
      Authentication: formattedAuthenticatedAt,
      Expiration: formattedExpiredAt,
      Error: format(new Date(), TIMESTAMP_FORMAT, { locale: ptBR }),
    });
    scope.setContext('Product Key', {
      Value: productKey.substring(0, 8),
      Product: getProduct(productKey),
    });
    scope.setTag('url', url);
    scope.setTag('resource', resource);
    if (isNetworkError) scope.setContext('Network', getNetworkInfo(error.meta?.retries));
    Sentry.captureMessage(fullTitle);
  });
}

export function sendInterfaceErrorToSentry(
  title: string,
  description: string,
  error: unknown,
  options: {
    tags?: Record<string, string>,
    fingerprint?: string[],
    /** Nível do evento; `warning` para o que merece atenção sem ser bug. Padrão: `error`. */
    level?: 'error' | 'warning',
  } = {},
) {
  const fullTitle = `[INTERFACE ERROR] ${title}`;
  const { id, username, userIsAdmin } = getUserData();

  Sentry.withScope((scope) => {
    scope.setLevel(options.level ?? 'error');
    scope.setFingerprint(options.fingerprint ?? ['interface', title]);
    Object.entries(options.tags ?? {}).forEach(([key, value]) => scope.setTag(key, value));
    scope.setTransactionName(description);
    scope.setUser({
      id,
      username,
      email: username,
      Admin: userIsAdmin ? '✅' : '❌',
    });
    scope.setContext('Details', {
      Description: description,
      Error: stringifyError(error),
    });
    Sentry.captureMessage(fullTitle);
  });
}
